import _ from 'lodash';
import {createSelector} from 'reselect';

import {canBePinnedToTeammateWorkspace} from '../../../helpers/models/customViewHelpers';
import {AppParametricSelector, AppSelector, wrapInShallowSelector} from '../../../helpers/reselectHelpers';
import {
  buildVisualCustomViewsPresorted,
  defaultIsCustomViewExpanded,
} from '../../../helpers/visualCustomViewHelpers';
import {DelegationModel} from '../../../models/api/delegationModel';
import {TagModel} from '../../../models/api/tagModel';
import {AppState} from '../../appState';
import {getFindIsExpanded} from '../localPreferences/localPreferencesSelectors';
import {getTags} from '../tags/tagsSelectors';
import {alphabetizeFlatTags} from '../workspace/sort/workspaceTagSortHelpers';
import {getVisibleCustomViewIds} from './preferencesSelectors';

type DelegationSelector<P> = AppParametricSelector<P, DelegationModel>;

type TagsSelector = AppSelector<ReadonlyArray<TagModel>>;

/*
 * Public selectors.
 */

export function getCustomViews(state: AppState) {
  return state.data.customViews;
}

/** Selects tags visible in the personal workspace with visibility based upon user's preferences. */
export const getVisibleTagsFromPreferences: TagsSelector = wrapInShallowSelector(
  createSelector([getVisibleCustomViewIds, getTags], (visibleCustomViewIds, tags) =>
    _(visibleCustomViewIds)
      .map((id) => tags[id])
      .compact()
      .filter((tag) => canBePinnedToTeammateWorkspace(tag))
      .value(),
  ),
);

function makeGetDelegationCustomViewTagsAlphabetized<P>(getDelegation: DelegationSelector<P>) {
  return createSelector([getDelegation, getTags], (delegation, tags) => {
    const unsortedVisibleTags = _(delegation.customViewIds)
      .map((id) => tags[id])
      .compact()
      .value();
    return alphabetizeFlatTags(unsortedVisibleTags);
  });
}

export function makeGetDelegationVisualCustomViews<P>(getDelegation: DelegationSelector<P>) {
  return createSelector(
    [
      getDelegation,
      getCustomViews,
      getFindIsExpanded,
      makeGetDelegationCustomViewTagsAlphabetized(getDelegation),
    ],
    (delegation, customViews, findIsExpanded, alphabetizedTags) =>
      buildVisualCustomViewsPresorted({
        customViews,
        presortedSelectableTags: alphabetizedTags.map((tag) => ({tag, isSelected: true})),
        parentTagOverridesMap: undefined,
        defaultExpandedState: defaultIsCustomViewExpanded,
        findIsExpanded,
      }),
  );
}
